import { LogPointsRuntimeHelper, RuntimeMajor, RuntimeSemver, RuntimeSupport } from './runtime/LogPointsRuntimeHelper'
import { LogPointsMuleThreeHelper } from './runtime/LogPointsMuleThreeHelper'
import { LogPointsMuleFourHelper } from './runtime/LogPointsMuleFourHelper'
import { foldLeft } from '../../../util/Object'
import APIManagerRemoteApi, { APIPolicyBody } from '../../../remote-api/APIManagerRemoteApi'
import ARMRemoteApi from '../../../remote-api/ARMRemoteApi'

import {
  API,
  APIDetail,
  APIPolicy,
  ARMApplicationDetail,
  ARMHybridDetail,
  RTFApplication,
  ARMLoggingConfiguration,
  ARMLogLevel,
  ARMTarget,
  Asset,
  AssetResponse,
  ConnectorIcon, isSplitMessageLoggingPolicyConfiguration,
  LogPointsConnectors,
  MessageLoggingPolicyConfiguration,
  ScopeLogging, SplitMessageLoggingPolicyConfiguration
} from '../../../remote-api/models'

import {
  getApplicationName,
  LogPointCategory,
  LogPointType,
  supportedConfigurations,
  supportedConfigurationsKeys
} from './LoggingPolicyConfigurations'

import {
  isVersionSupported,
  parseSemver
} from '@mulesoft/monitoring-center-components/lib/components/SupportedRuntimesHelper/SupportedRuntimesHelper'

import {
  ApplicationResource,
  DetailedResource,
  getResourceId,
  getResourceMuleVersion,
  getResourceName,
  isAPIDetail,
  isApplicationResource,
  isARMApplicationDetail,
  isARMHybridDetail,
  isCloudHub2Application,
  isRTFApplication,
  Resource
} from '../../../common/resource/model'

import {
  getAppLogLevelsByResource,
  getMostRestrictiveLogLevel,
  LogLevel,
  LogPointsConnectorsState
} from '../LogPointsBody/LogPointsApplicationsBody/model'
import { LogPointsFlexGatewayHelper } from './runtime/LogPointsFlexGatewayHelper'
import { isFlex } from '../../../util/flex'

export const EMPTY_LOG_POINT_STATES: LogPointStates = {}

export type LogPointStates = {
  [key: string]: {
    type: LogPointType
    category: LogPointCategory
    label: string
    enabled: boolean
  }
}

export enum APIManagingType {
  BASIC = 'BASIC',
  CLOUDHUB_PROXY = 'CLOUDHUB_PROXY',
  HYBRID_PROXY = 'HYBRID_PROXY',
  RUNTIME_FABRIC_PROXY = 'RUNTIME_FABRIC_PROXY',
  OTHER = 'OTHER'
}

export type MuleRuntimeSupport = {
  mule: RuntimeMajor
  versionSupport: RuntimeSupport
}

export class LogPointsService {

  public static readonly appsMinMuleVersion: RuntimeSemver = {major: 3, minor: 8, patch: 5}

  public static validateApplicationsCompatibility(applications: ApplicationResource[]): ApplicationResource[] {
    return applications.filter(app => LogPointsService.validateApplicationCompatibility(app))
  }

  public static validateApplicationCompatibility(resource: ApplicationResource) {
    if (!isCloudHub2Application(resource) && isRTFApplication(resource))return false

    const muleVersion = getResourceMuleVersion(resource)
    if (!muleVersion) return false

    const runtimeSemver = parseSemver(muleVersion)
    if (!runtimeSemver) return false

    return isVersionSupported(runtimeSemver, LogPointsService.appsMinMuleVersion)
  }

  private static getManagingType(detail: APIDetail) {
    const deploymentType = detail.endpoint && detail.endpoint.deploymentType
    const proxyUri = detail.endpoint && detail.endpoint.proxyUri

    if (detail.deployment || proxyUri) {
      switch (deploymentType) {
        case 'CH':
          return APIManagingType.CLOUDHUB_PROXY

        case 'HY':
          return APIManagingType.HYBRID_PROXY

        case 'RF':
          return APIManagingType.RUNTIME_FABRIC_PROXY

        default:
          return APIManagingType.OTHER
      }
    }

    return APIManagingType.BASIC
  }

  private readonly remoteAPI: APIManagerRemoteApi
  private readonly remoteARM: ARMRemoteApi
  private readonly runtimeHelpers: LogPointsRuntimeHelper[]

  constructor(remoteAPIManager: APIManagerRemoteApi, remoteARM: ARMRemoteApi) {
    this.remoteAPI = remoteAPIManager
    this.remoteARM = remoteARM

    this.runtimeHelpers = [
      new LogPointsMuleThreeHelper(this.remoteAPI),
      new LogPointsMuleFourHelper(this.remoteAPI),
      new LogPointsFlexGatewayHelper(this.remoteAPI)
    ]
  }

  public async getApplicationFullDomain(orgId: string,
                                        envId: string,
                                        apiId: string,
                                        apis?: API[]): Promise<string> {
    const appName = apis && getApplicationName(apis, apiId)
    if (!appName) return Promise.reject('Cannot fetch app data without an app name in API')

    const isCloudHubEvaluator = ({deployment, endpoint}: APIDetail) => {
      return !!(deployment && endpoint && endpoint.isCloudHub)
    }

    const fetchApp = (shouldFetch: boolean) => shouldFetch ?
      this.getCloudHubAppData(orgId, envId, appName) :
      Promise.reject('Cannot not fetch app data to get application full domain')

    const getFullDomain = (app: ARMApplicationDetail | null) => app ?
      Promise.resolve(app.fullDomain) :
      Promise.reject('Cannot get full domain from null CH response')

    return this.fetchAPI(orgId, envId, apiId)
      .then(isCloudHubEvaluator)
      .then(fetchApp)
      .then(getFullDomain)
  }

  public async fetchRuntimesSupport(orgId: string): Promise<MuleRuntimeSupport[]> {
    const supportMapper = async (helper: LogPointsRuntimeHelper) => {
      const runtimeSupport = await helper.fetchRuntimeSupport(orgId)
      return {mule: helper.runtime, versionSupport: runtimeSupport}
    }

    return Promise.all(this.runtimeHelpers.map(supportMapper))
  }

  public async fetchAssetsDetails(orgId: string, envId: string, assets: Asset[]): Promise<APIDetail[]> {
    const assetMapper = (asset: Asset) => this.fetchAssetAPIDetails(orgId, envId, asset)
    const arrayOfDetails = await Promise.all(assets.map(assetMapper))

    return [].concat.apply([], arrayOfDetails) as APIDetail[]
  }

  public async validateAssetsCompatibility(assets: Asset[], details: APIDetail[], runtimeSupport: MuleRuntimeSupport[]): Promise<Asset[]> {
    const assetMapper = async (asset: Asset) => {
      const apis = await this.validateAPIsCompatibility(asset.apis, details, runtimeSupport)
      return {...asset, apis}
    }

    const assetsWithValidAPIs = await Promise.all(assets.map(assetMapper))
    const assetFilter = (asset: Asset) => asset.apis.length > 0

    return assetsWithValidAPIs.filter(assetFilter)
  }

  public async validateDetailedResourceCompatibility(orgId: string, detailedResource: DetailedResource) {
    const apiValidator = async (apiDetail: APIDetail) => {
      const runtimeSupport = await this.fetchRuntimesSupport(orgId)
      return this.validateAPIDetailCompatibility(apiDetail, runtimeSupport)
    }

    if (isApplicationResource(detailedResource)) return LogPointsService.validateApplicationCompatibility(detailedResource)
    else if (isAPIDetail(detailedResource)) return apiValidator(detailedResource)
    else return false
  }

  public async validateAPIDetailCompatibility(apiDetail: APIDetail, rtSupport: MuleRuntimeSupport[]) {
    const apiDetailManagingType = LogPointsService.getManagingType(apiDetail)

    if (isFlex(apiDetail)) return true
    else if (apiDetailManagingType === APIManagingType.BASIC) return true
    else if (apiDetailManagingType === APIManagingType.OTHER) return false
    else if (apiDetailManagingType === APIManagingType.RUNTIME_FABRIC_PROXY) return false

    const deployment = apiDetail.deployment
    if (!deployment) return false

    const gatewayVersion = deployment.gatewayVersion
    const muleVersion4OrAbove = !!(apiDetail.endpoint && apiDetail.endpoint.muleVersion4OrAbove)
    const apiGatewayCompatible = this.validateGatewayCompatibility(rtSupport, muleVersion4OrAbove, gatewayVersion)
    if (apiGatewayCompatible) return true

    const orgId = apiDetail.organizationId
    const envId = apiDetail.environmentId

    switch (apiDetailManagingType) {
      case APIManagingType.CLOUDHUB_PROXY:
        const appName = deployment.applicationName
        return await this.validateCHProxyCompatibility(orgId, envId, appName, rtSupport, muleVersion4OrAbove)

      case APIManagingType.HYBRID_PROXY:
        const hybridAppId = deployment.applicationId
        if (!hybridAppId) return false

        return await this.validateHYProxyCompatibility(orgId, envId, hybridAppId, rtSupport, muleVersion4OrAbove)

      default:
        return false
    }
  }

  public async fetchMessageLoggingConfiguration(orgId: string, envId: string, versionId: string): Promise<MessageLoggingPolicyConfiguration[]> {
    const policy = await this.fetchMessageLoggingPolicy(orgId, envId, versionId)

    if (policy) {
      return isSplitMessageLoggingPolicyConfiguration(policy.configurationData) ?
          policy.configurationData.loggingConfiguration : policy.configurationData
    } else {
      return []
    }
  }

  public async fetchAPIPolicyBody(orgId: string, envId: string, apiId: string, logPoints: LogPointStates): Promise<APIPolicyBody | undefined> {
    const policy = await this.fetchMessageLoggingPolicy(orgId, envId, apiId)
    const api = await this.fetchAPI(orgId, envId, apiId)
    const helper = this.runtimeHelpers.find(helper => helper.canHandle(api))

    if (!helper) {
      return undefined
    }

    const nonLogPointsConfigs = this.getNonLogPointsConfigs(policy && policy.configurationData)
    const logPointsConfigs = this.getMessageLoggingConfiguration(logPoints)
    const configurations = [...logPointsConfigs, ...nonLogPointsConfigs]

    return helper.getAPIPolicyBody(api, configurations, policy)
  }

  public fetchAPIs(orgId: string, envId: string, offset?: number): Promise<AssetResponse> {
    return this.remoteAPI.fetchAPIs(orgId, envId, {limit: 100, offset: offset ? offset : 0})
  }

  public getLogPointStates(configurations: MessageLoggingPolicyConfiguration[]): LogPointStates {
    const categories = configurations.map(config => config.itemData.category)
    const validCategories = categories.filter(category => category !== undefined)
    const configSet = new Set(validCategories)
    const states: LogPointStates = {}

    supportedConfigurationsKeys
      .forEach(key => {
        const {type, category, label} = supportedConfigurations[key]
        const enabled = configSet.has(key)
        states[key] = {type, category, label, enabled}
      })

    return states
  }

  public saveLogPoints(orgId: string, envId: string, apiId: string, body: APIPolicyBody): Promise<APIPolicy> {

    return body.id ?
      this.remoteAPI.updateAPIPolicy(orgId, envId, apiId, body.id.toString(), body) :
      this.remoteAPI.createAPIPolicy(orgId, envId, apiId, body)
  }

  public deleteAPIPolicy(orgId: string, envId: string, apiId: string, policyId: string): Promise<void> {
    return this.remoteAPI.deleteAPIPolicy(orgId, envId, apiId, policyId)
  }

  public fetchAPI(orgId: string, envId: string, api: string): Promise<APIDetail> {
    return this.remoteAPI.fetchAPI(orgId, envId, api)
  }

  public fetchLogPointsConnectors(orgId: string, envId: string, connectors: LogPointsConnectors, resource: Resource, targetId?: string):
    Promise<LogPointsConnectorsState> {
      if (targetId) {
        return this.fetchHybridLogPointsConnectors(orgId, envId, targetId, connectors, resource)
      } else if (isCloudHub2Application(resource)) {
        return this.fetchCloudHub2LogPointsConnectors(orgId, envId, connectors, resource)
      } else {
        return this.fetchCloudHubLogPointsConnectors(orgId, envId, connectors, resource)
      }
  }

  public saveLogPointsConnectors(orgId: string,
                                 envId: string,
                                 resource: Resource,
                                 logPointsConnectors: LogPointsConnectorsState,
                                 targetId?: string): Promise<LogPointsConnectorsState> {
      if (targetId) {
        return this.saveHybridLogPointsConnectors(orgId, envId, targetId, logPointsConnectors, resource)
      } else if (isCloudHub2Application(resource)){
        return this.saveCloudHub2LogPointsConnectors(orgId, envId, resource, logPointsConnectors)
      } else {
        return this.saveCloudHubLogPointsConnectors(orgId, envId, resource, logPointsConnectors)
      }
  }

  public static isBasicEndpointAPI(apiDetail: APIDetail): boolean {
    const managingType = LogPointsService.getManagingType(apiDetail)

    return managingType === APIManagingType.BASIC;
  }

  private saveCloudHubLogPointsConnectors(orgId: string, envId: string, resource: Resource, connectors: LogPointsConnectorsState):
    Promise<LogPointsConnectorsState> {
      if (isARMApplicationDetail(resource)) {
        const application = this.mergeCloudHubLoggingConf(connectors, resource)

        return this.remoteARM.putApplication(orgId, application, envId).then(() => {
          return Promise.resolve(connectors)
        })
      } else {
        return Promise.reject('It is not a CloudHub Application')
      }
  }

  private saveHybridLogPointsConnectors(orgId: string,
                                        envId: string,
                                        targetId: string,
                                        connectors: LogPointsConnectorsState,
                                        resource: Resource): Promise<LogPointsConnectorsState> {
      return this.remoteARM.fetchHybridLoggingComponent(orgId, envId, targetId).then(response => {
        const loggingConf = this.mergeHybridLoggingConf(connectors, response.data.configuration, resource)

        return this.remoteARM.updateHybridLoggingComponent(orgId, envId, targetId, loggingConf).then(() => {
          return Promise.resolve(connectors)
        })
      })
  }

  private saveCloudHub2LogPointsConnectors(orgId: string, envId: string, resource: RTFApplication, connectors: LogPointsConnectorsState):
      Promise<LogPointsConnectorsState> {
        const application = this.mergeCloudHub2LoggingConf(connectors, resource)
        if (application){
          return this.remoteARM.updateRTFApplication(orgId, application, envId, resource.id).then(() => {
            return Promise.resolve(connectors)
          })
        } else {
          return Promise.reject('No Data')
        }
  }

  public mergeCloudHubLoggingConf(logPointsConnectors: LogPointsConnectorsState, app: ARMApplicationDetail): ARMApplicationDetail {
    let logLevels = app.logLevels

    Object.keys(logPointsConnectors).forEach(connectorId => {
      const connectorState = logPointsConnectors[connectorId]

      const packages = connectorState.connector.packages

      packages.forEach(packageName => {
        const logLevelIndex = logLevels.findIndex(obj => obj && obj.packageName === packageName)

        if (logLevelIndex > -1) {
          if (connectorState.checked) {
            logLevels[logLevelIndex].level = connectorState.logLevel
          } else {
            logLevels.splice(logLevelIndex, 1)
          }
        } else if (connectorState.checked) {
          logLevels.push({packageName: packageName, level: connectorState.logLevel})
        }
      })
    })

    return {...app, logLevels: [...logLevels]}
  }

  public mergeHybridLoggingConf(logPointsConnectors: LogPointsConnectorsState,
                                loggingConf: ARMLoggingConfiguration,
                                resource: Resource): ARMLoggingConfiguration {
    const appLogLevels = loggingConf.applicationLogLevels
    const {logLevels, index} = getAppLogLevelsByResource(resource, loggingConf.applicationLogLevels)

    if (index > -1) {
      Object.keys(logPointsConnectors).forEach(connectorId => {
        const connectorState = logPointsConnectors[connectorId]

        const packages = connectorState.connector.packages

        packages.forEach(packageName => {
          const logLevelIndex = logLevels.findIndex(obj => obj && obj.scope === packageName)

          if (logLevelIndex > -1) {
            if (connectorState.checked) {
              logLevels[logLevelIndex].logLevel = connectorState.logLevel
            } else {
              logLevels.splice(logLevelIndex, 1)
            }
          } else if (connectorState.checked) {
            logLevels.push({scope: packageName, logLevel: connectorState.logLevel})
          }
        })
      })

      appLogLevels[index].scopeLoggingConfigurations = logLevels
    }

    return {...loggingConf, applicationLogLevels: [...appLogLevels]}
  }

  public mergeCloudHub2LoggingConf(connectors: LogPointsConnectorsState, resource: RTFApplication) {

    if (resource.application.configuration) {
      const logLevels = resource.application.configuration['mule.agent.logging.service']!.scopeLoggingConfigurations

      Object.keys(connectors).forEach(connectorId => {
        const connectorState = connectors[connectorId]

        const packages = connectorState.connector.packages

        packages.forEach(packageName => {
          const logLevelIndex = logLevels.findIndex(obj => obj && obj.scope === packageName)

          if (logLevelIndex > -1) {
            if (connectorState.checked) {
              logLevels[logLevelIndex].logLevel = connectorState.logLevel
            } else {
              logLevels.splice(logLevelIndex, 1)
            }
          } else if (connectorState.checked) {
            logLevels.push({scope: packageName, logLevel: connectorState.logLevel})
          }
        })
      })

      return resource
    } else {
      return undefined
    }
  }

  private fetchCloudHubLogPointsConnectors(orgId: string, envId: string, connectors: LogPointsConnectors, resource: Resource):
    Promise<LogPointsConnectorsState> {
    if (isARMApplicationDetail(resource)) {
      const muleVersion = getResourceMuleVersion(resource)
      if (!muleVersion) return Promise.resolve({})

      return this.remoteARM.fetchApplicationData(orgId, envId, getResourceName(resource)).then(updatedResource => {
        return Promise.resolve(this.createCloudHubLogPointsConnectors(connectors, updatedResource.logLevels, parseSemver(muleVersion)))
      }).catch(() => Promise.resolve(this.createCloudHubLogPointsConnectors(connectors, resource.logLevels, parseSemver(muleVersion))))
    } else {
      return Promise.resolve({})
    }
  }

  private fetchHybridLogPointsConnectors(orgId: string, envId: string, targetId: string, connectors: LogPointsConnectors, resource: Resource):
    Promise<LogPointsConnectorsState> {
    if (isARMHybridDetail(resource)) {
      const muleVersion = getResourceMuleVersion(resource)
      if (!muleVersion) return Promise.resolve({})

      return this.remoteARM.fetchHybridLoggingComponent(orgId, envId, targetId).then(response => {
        const loggingComponent = response.data
        const {logLevels} = getAppLogLevelsByResource(resource, loggingComponent.configuration.applicationLogLevels)

        return Promise.resolve(this.createLogPointsConnectors(connectors, logLevels, parseSemver(muleVersion)))
      }).catch(() => Promise.resolve({}))
    } else {
      return Promise.resolve({})
    }
  }

  private fetchCloudHub2LogPointsConnectors(orgId: string, envId: string, connectors: LogPointsConnectors, resource: Resource):
      Promise<LogPointsConnectorsState> {
      const muleVersion = getResourceMuleVersion(resource)
      if (!muleVersion) return Promise.resolve({})
      return this.remoteARM.fetchRTFApplication(orgId, envId, getResourceId(resource)).then(updatedResource => {
        if (updatedResource.application.configuration){
          return Promise.resolve(
              this.createLogPointsConnectors(connectors,
                                             updatedResource.application.configuration['mule.agent.logging.service']!.scopeLoggingConfigurations,
                                             parseSemver(muleVersion)))
        } else {
          return Promise.resolve({})
        }
      }).catch(() => Promise.resolve({}))
  }

  private createCloudHubLogPointsConnectors(connectors: LogPointsConnectors, logLevels: ARMLogLevel[], muleVersion?: RuntimeSemver):
    LogPointsConnectorsState {
    const muleConnectors = muleVersion && muleVersion.major < 4 ? connectors.mule3Icons : connectors.mule4Icons

    return foldLeft(muleConnectors, {})(this.logPointsCloudHubConnectorFromConnector(logLevels))
  }

  private createLogPointsConnectors(connectors: LogPointsConnectors, logLevels: ScopeLogging[] , muleVersion?: RuntimeSemver):
    LogPointsConnectorsState {
    const muleConnectors = muleVersion && muleVersion.major < 4 ? connectors.mule3Icons : connectors.mule4Icons

    return foldLeft(muleConnectors, {})(this.logPointsConnectorFromConnector(logLevels))
  }

  private logPointsCloudHubConnectorFromConnector(logLevels: ARMLogLevel[]) {
    return (result: LogPointsConnectorsState, connector: ConnectorIcon) => {
      const packages = connector.packages
      const possibleLogLevels: LogLevel[] = []
      packages.forEach(packageName => {
        const resourceConnector = logLevels.find(logLevel => logLevel.packageName === packageName)
        if (resourceConnector) possibleLogLevels.push(resourceConnector.level as LogLevel)
      })

      return {
        ...result,
        [connector.id]: {
          connector: connector,
          logLevel: getMostRestrictiveLogLevel(possibleLogLevels),
          checked: possibleLogLevels.length > 0,
          initialChecked: possibleLogLevels.length > 0
        }
      }
    }
  }

  private logPointsConnectorFromConnector(logLevels: ScopeLogging[]) {
    return (result: LogPointsConnectorsState, connector: ConnectorIcon) => {
      const packages = connector.packages
      const possibleLogLevels: LogLevel[] = []
      packages.forEach(packageName => {
        const resourceConnector = logLevels.find(logLevel => logLevel.scope === packageName)
        if (resourceConnector) possibleLogLevels.push(resourceConnector.logLevel as LogLevel)
      })

      return {
        ...result,
        [connector.id]: {
          connector: {...connector},
          logLevel: getMostRestrictiveLogLevel(possibleLogLevels),
          checked: possibleLogLevels.length > 0,
          initialChecked: possibleLogLevels.length > 0
        }
      }
    }
  }

  private getMessageLoggingConfiguration(logPoints: LogPointStates): MessageLoggingPolicyConfiguration[] {
    return Object.keys(logPoints)
      .filter(key => logPoints[key].enabled)
      .map(key => supportedConfigurations[key].config)
  }

  private getNonLogPointsConfigs(configs?: MessageLoggingPolicyConfiguration[] | SplitMessageLoggingPolicyConfiguration)
      : MessageLoggingPolicyConfiguration[] {
    if (!configs) {
      return []
    }

    const loggingConfiguration = isSplitMessageLoggingPolicyConfiguration(configs) ? configs.loggingConfiguration : configs

    return loggingConfiguration.filter(config => {
      const category = config.itemData && config.itemData.category
      return category === undefined || supportedConfigurationsKeys.indexOf(category) < 0
    })
  }

  private async validateAPIsCompatibility(apis: API[], details: APIDetail[], rtSupport: MuleRuntimeSupport[]) {
    const apiValidator = (api: API) => this.validateAPICompatibility(api, details, rtSupport)
    const apiWithValidations = await Promise.all(apis.map(apiValidator))
    const apiWithPositiveValidation = apiWithValidations.filter(apiWithValidation => apiWithValidation.valid)
    const validatedAPIs = apiWithPositiveValidation.map(apiWithValidation => apiWithValidation.api)

    return validatedAPIs.map(this.getApplicationNameMapper(details))
  }

  private getApplicationNameMapper(details: APIDetail[]) {
    return (api: API) => {
      const apiDetail = details.find(detail => detail.id.toString() === api.id.toString())

      api.applicationName = apiDetail && apiDetail.deployment ? apiDetail.deployment.applicationName : undefined

      return api
    }
  }

  private async validateAPICompatibility(api: API, details: APIDetail[], rtSupport: MuleRuntimeSupport[]) {
    const maybeApiDetail = details.find(detail => detail.id.toString() === api.id.toString())
    if (!maybeApiDetail) return {valid: false, api}

    const valid = await this.validateAPIDetailCompatibility(maybeApiDetail, rtSupport)
    return {valid, api}
  }

  private async validateHYProxyCompatibility(orgId: string,
                                             envId: string,
                                             appId: string,
                                             rtSupport: MuleRuntimeSupport[],
                                             muleVersion4OrAbove: boolean) {
    const hybridAppData = await this.getHybridAppData(orgId, envId, appId)
    const target = hybridAppData && hybridAppData.target && hybridAppData.target
    if (!target) return false

    if (target.servers !== undefined) {
      return target.servers.every(this.getARMTargetCompatibilityValidator(rtSupport, muleVersion4OrAbove))
    } else {
      return this.validateARMTargetCompatibility(target, rtSupport, muleVersion4OrAbove)
    }
  }

  private getARMTargetCompatibilityValidator(rtSupport: MuleRuntimeSupport[], muleVersion4OrAbove: boolean) {
    return (target: ARMTarget) => this.validateARMTargetCompatibility(target, rtSupport, muleVersion4OrAbove)
  }

  private validateARMTargetCompatibility(target: ARMTarget,
                                         rtSupport: MuleRuntimeSupport[],
                                         muleVersion4OrAbove: boolean) {
    const version = target.muleVersion || target.gatewayVersion
    return version !== undefined ? this.validateGatewayCompatibility(rtSupport, muleVersion4OrAbove, version) : false
  }

  private async validateCHProxyCompatibility(orgId: string,
                                             envId: string,
                                             appName: string,
                                             rtSupport: MuleRuntimeSupport[],
                                             muleVersion4OrAbove: boolean) {
    const chAppData = await this.getCloudHubAppData(orgId, envId, appName)
    const chVersion = chAppData && chAppData.muleVersion && chAppData.muleVersion.version
    if (!chVersion) return false

    return this.validateGatewayCompatibility(rtSupport, muleVersion4OrAbove, chVersion)
  }

  private async getCloudHubAppData(orgId: string, envId: string, appName: string): Promise<ARMApplicationDetail | null> {
    return new Promise<ARMApplicationDetail | null>((resolve) => {
      this.remoteARM.fetchApplicationData(orgId, envId, appName)
        .then(response => resolve(response))
        .catch(() => resolve(null))
    })
  }

  private async getHybridAppData(orgId: string, envId: string, appId: string): Promise<ARMHybridDetail | null> {
    return new Promise<ARMHybridDetail | null>((resolve) => {
      this.remoteARM.fetchHybridData(orgId, envId, appId)
        .then(response => resolve(response.data))
        .catch(() => resolve(null))
    })
  }

  private validateGatewayCompatibility(runtimeSupport: MuleRuntimeSupport[], muleVersion4OrAbove: boolean, gatewayVersion?: string): boolean {
    const gatewaySemver = gatewayVersion && parseSemver(gatewayVersion)
    if (!gatewaySemver) return false

    const runtime = LogPointsRuntimeHelper.getMajor(gatewaySemver.major)
    if (muleVersion4OrAbove && runtime === RuntimeMajor.MULE_THREE) return false
    else if (!muleVersion4OrAbove && runtime === RuntimeMajor.MULE_FOUR) return false

    const maybeSupport = runtimeSupport.find(support => support.mule === runtime)
    if (!maybeSupport) return false

    return isVersionSupported(gatewaySemver, maybeSupport.versionSupport.minMuleVersion)
  }

  private fetchAssetAPIDetails(orgId: string, envId: string, asset: Asset): Promise<APIDetail[]> {
    return this.fetchAPIsDetails(orgId, envId, asset.apis)
  }

  private fetchAPIsDetails(orgId: string, envId: string, apis: API[]): Promise<APIDetail[]> {
    return Promise.all(apis.map(api => this.fetchAPI(orgId, envId, api.id.toString())))
  }

  private async fetchMessageLoggingPolicy(orgId: string, envId: string, versionId: string): Promise<APIPolicy | undefined> {
    const api = await this.fetchAPI(orgId, envId, versionId)
    const helper = this.runtimeHelpers.find(helper => helper.canHandle(api))

    if (!helper) {
      return undefined
    }

    return helper.fetchMessageLoggingPolicy(orgId, envId, versionId)
  }
}
